import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Colors, Flex, Typography } from '@banhala/ui-kit';
import type { OverviewCategory } from 'shared/src/api';
import { useLogClickExpandableCategoryList } from '../hooks/useLogClickExpandableCategoryList';
import { bridge } from 'shared/src/utils';

const MENU_ITEM_HEIGHT = 40;

interface SideMenuProps {
  categories: OverviewCategory[];
  activeIndex: number;
  scrollViewRef: React.RefObject<ScrollView | null>;
  onMenuPress: (index: number) => void;
}

// native (Android `toCategoryTabList`, iOS `OverviewCategoryGroup`)와 동일하게
// `index.name`(없으면 category.name) 기준으로 distinct 그룹 탭을 구성한다.
interface CategoryTab {
  key: string;
  category: OverviewCategory;
  firstCategoryIndex: number;
}

const getTabKey = (category: OverviewCategory): string =>
  category.item.index?.name ?? category.item.name;

export const SideMenu = ({
  categories,
  activeIndex,
  scrollViewRef,
  onMenuPress,
}: SideMenuProps) => {
  const itemRefs = useRef<(View | null)[]>([]);

  const tabList = useMemo<CategoryTab[]>(() => {
    const result: CategoryTab[] = [];
    const seen = new Set<string>();
    categories.forEach((category, index) => {
      const key = getTabKey(category);
      if (seen.has(key)) return;
      seen.add(key);
      result.push({ key, category, firstCategoryIndex: index });
    });
    return result;
  }, [categories]);

  const activeTabIndex = useMemo(() => {
    const activeCategory = categories[activeIndex];
    if (activeCategory == null) return 0;
    const activeKey = getTabKey(activeCategory);
    const idx = tabList.findIndex(tab => tab.key === activeKey);
    return idx >= 0 ? idx : 0;
  }, [tabList, activeIndex, categories]);

  useEffect(() => {
    const targetY = activeTabIndex * MENU_ITEM_HEIGHT;
    scrollViewRef.current?.scrollTo({
      y: targetY - MENU_ITEM_HEIGHT * 2,
      animated: true,
    });
    bridge.vibrate('light');
  }, [activeTabIndex, scrollViewRef]);

  const logClickExpandableCategoryList = useLogClickExpandableCategoryList();

  const handlePress = useCallback(
    (tab: CategoryTab) => () => {
      bridge.vibrate('light');
      logClickExpandableCategoryList(tab.category);
      onMenuPress(tab.firstCategoryIndex);
    },
    [onMenuPress, logClickExpandableCategoryList],
  );

  return (
    <ScrollView
      ref={scrollViewRef}
      style={styles.container}
      showsVerticalScrollIndicator={false}
    >
      {tabList.map((tab, tabIndex) => (
        <Flex
          key={tab.key}
          ref={(el: View | null) => {
            itemRefs.current[tabIndex] = el;
          }}
          height={MENU_ITEM_HEIGHT}
          justifyContent="center"
          padding={{ horizontal: 16 }}
          backgroundColor={tabIndex === activeTabIndex ? 'white' : 'bg_secondary'}
          onPress={handlePress(tab)}
        >
          <Typography
            type={tabIndex === activeTabIndex ? 'subtitle3' : 'body3'}
            color={
              tabIndex === activeTabIndex
                ? 'content_primary'
                : 'content_secondary'
            }
            numberOfLines={1}
          >
            {tab.key}
          </Typography>
        </Flex>
      ))}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.bg_secondary,
  },
});
